import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  InputNumber,
  message,
  Space,
  Tag,
  Popconfirm,
  Card,
  Row,
  Col,
  Statistic,
  Select,
  Drawer,
  Descriptions,
  Divider
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ThunderboltOutlined,
  EnvironmentOutlined,
  PhoneOutlined,
  PoweroffOutlined
} from '@ant-design/icons';
import { project_api } from '../api/project-api';
import { axiosClient } from '../api/axios_client';

const { TextArea } = Input;
const { Option } = Select;

export default function Projects() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [inverters, setInverters] = useState([]);
  const [availableInverters, setAvailableInverters] = useState([]);
  const [form] = Form.useForm();
  const [editingProject, setEditingProject] = useState(null);
  const [viewportWidth, setViewportWidth] = useState(window.innerWidth);

  useEffect(() => {
    const handleResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const isMobile = viewportWidth < 768;
  const drawerWidth = isMobile ? '100%' : 800;

  // Load projects
  const loadProjects = async () => {
    try {
      setLoading(true);
      const response = await project_api.getAllProjects({ limit: 1000 });
      if (response.success) {
        setProjects(response.data || []);
      }
    } catch (error) {
      message.error('Không thể tải danh sách dự án: ' + (error.message || 'Lỗi không xác định'));
    } finally {
      setLoading(false);
    }
  };

  // Load available inverters (chưa thuộc project nào)
  const loadAvailableInverters = async () => {
    try {
      const response = await axiosClient.get('/api/inverters?limit=1000');
      if (response.success) {
        // Lọc các inverter chưa có project_id hoặc project_id = null
        const available = (response.data || []).filter(inv => !inv.project_id);
        setAvailableInverters(available);
      }
    } catch (error) {
      console.error('Error loading available inverters:', error);
    }
  };

  useEffect(() => {
    loadProjects();
  }, []);

  // Load inverters của project khi mở drawer
  useEffect(() => {
    if (drawerVisible && selectedProject) {
      loadProjectInverters(selectedProject.id);
      loadAvailableInverters();
    }
  }, [drawerVisible, selectedProject]);

  const loadProjectInverters = async (projectId) => {
    try {
      const response = await project_api.getProjectInverters(projectId);
      if (response.success) {
        setInverters(response.data || []);
      }
    } catch (error) {
      message.error('Không thể tải danh sách inverter: ' + (error.message || 'Lỗi không xác định'));
    }
  };

  const handleCreate = () => {
    setEditingProject(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (project) => {
    setEditingProject(project);
    const parsedLocation = project.location || {};
    form.setFieldsValue({
      name: project.name,
      address: project.address || '',
      phone_number: project.phone_number || '',
      total_pv_capacity: project.total_pv_capacity || 0,
      total_inverter_capacity: project.total_inverter_capacity || 0,
      description: project.description || '',
      status: project.status || 'active',
      image_url: project.image_url || '',
      location: {
        latitude: parsedLocation.latitude ?? '',
        longitude: parsedLocation.longitude ?? '',
      }
    });
    setModalVisible(true);
  };

  const handleDelete = async (id) => {
    try {
      const response = await project_api.deleteProject(id);
      if (response.success) {
        message.success('Xóa dự án thành công!');
        loadProjects();
      } else {
        message.error(response.message || 'Xóa dự án thất bại');
      }
    } catch (error) {
      message.error('Xóa dự án thất bại: ' + (error.message || 'Lỗi không xác định'));
    }
  };

  const handleSubmit = async (values) => {
    try {
      setLoading(true);
      let response;
      
      if (editingProject) {
        response = await project_api.updateProject(editingProject.id, values);
      } else {
        response = await project_api.createProject(values);
      }

      if (response.success) {
        message.success(editingProject ? 'Cập nhật dự án thành công!' : 'Tạo dự án thành công!');
        setModalVisible(false);
        form.resetFields();
        loadProjects();
      } else {
        message.error(response.message || 'Thao tác thất bại');
      }
    } catch (error) {
      message.error('Thao tác thất bại: ' + (error.message || 'Lỗi không xác định'));
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = async (project) => {
    setSelectedProject(project);
    setDrawerVisible(true);
  };

  const handleAddInverter = async (inverterId) => {
    try {
      const response = await project_api.addInverterToProject(selectedProject.id, inverterId);
      if (response.success) {
        message.success('Thêm inverter vào dự án thành công!');
        loadProjectInverters(selectedProject.id);
        loadAvailableInverters();
        // Reload project để cập nhật tổng công suất
        const projectResponse = await project_api.getProjectById(selectedProject.id);
        if (projectResponse.success) {
          setSelectedProject(projectResponse.data);
        }
      } else {
        message.error(response.message || 'Thêm inverter thất bại');
      }
    } catch (error) {
      message.error('Thêm inverter thất bại: ' + (error.message || 'Lỗi không xác định'));
    }
  };

  const handleRemoveInverter = async (inverterId) => {
    try {
      const response = await project_api.removeInverterFromProject(selectedProject.id, inverterId);
      if (response.success) {
        message.success('Xóa inverter khỏi dự án thành công!');
        loadProjectInverters(selectedProject.id);
        loadAvailableInverters();
        // Reload project để cập nhật tổng công suất
        const projectResponse = await project_api.getProjectById(selectedProject.id);
        if (projectResponse.success) {
          setSelectedProject(projectResponse.data);
        }
      } else {
        message.error(response.message || 'Xóa inverter thất bại');
      }
    } catch (error) {
      message.error('Xóa inverter thất bại: ' + (error.message || 'Lỗi không xác định'));
    }
  };

  const columns = [
    {
      title: 'Tên dự án',
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => a.name.localeCompare(b.name),
      ellipsis: true,
    },
    {
      title: 'Địa chỉ',
      dataIndex: 'address',
      key: 'address',
      responsive: ['md'],
      ellipsis: true,
    },
    {
      title: 'SĐT',
      dataIndex: 'phone_number',
      key: 'phone_number',
      width: 110,
      ellipsis: true,
    },
    {
      title: 'Số inv',
      dataIndex: 'inverter_count',
      key: 'inverter_count',
      align: 'center',
      width: 110,
      render: (count) => <Tag color="blue">{count || 0}</Tag>,
    },
    {
      title: 'PV (kW)',
      dataIndex: 'total_pv_capacity',
      key: 'total_pv_capacity',
      align: 'right',
      width: 110,
      render: (value) => value ? parseFloat(value).toFixed(2) : '0.00',
    },
    {
      title: 'Inv (kW)',
      dataIndex: 'total_inverter_capacity',
      key: 'total_inverter_capacity',
      align: 'right',
      width: 110,
      render: (value) => value ? parseFloat(value).toFixed(2) : '0.00',
    },
    {
      title: 'Trạng thái',
      dataIndex: 'status',
      key: 'status',
      width: 80,
      render: (status) => {
        const statusMap = {
          active: { color: 'green', text: 'Hoạt động' },
          inactive: { color: 'default', text: 'Tạm dừng' },
          completed: { color: 'blue', text: 'Hoàn thành' },
          cancelled: { color: 'red', text: 'Hủy' },
        };
        const statusInfo = statusMap[status] || { color: 'default', text: status };
        return <Tag color={statusInfo.color}>{statusInfo.text}</Tag>;
      },
    },
    {
      title: 'Thao tác',
      key: 'action',
      width: 200,
      render: (_, record) => (
        <Space size="middle">
          <Button
            type="link"
            icon={<ThunderboltOutlined />}
            onClick={() => handleViewDetails(record)}
          >
            Chi tiết
          </Button>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            Sửa
          </Button>
          <Popconfirm
            title="Bạn có chắc muốn xóa dự án này?"
            onConfirm={() => handleDelete(record.id)}
            okText="Xóa"
            cancelText="Hủy"
          >
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
            >
              Xóa
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // Tính tổng thống kê
  const stats = {
    total: projects.length,
    active: projects.filter(p => p.status === 'active').length,
    totalPvCapacity: projects.reduce((sum, p) => {
      const val = parseFloat(p.total_pv_capacity) || 0;
      return sum + (isNaN(val) ? 0 : val);
    }, 0),
    totalInverterCapacity: projects.reduce((sum, p) => {
      const val = parseFloat(p.total_inverter_capacity) || 0;
      return sum + (isNaN(val) ? 0 : val);
    }, 0),
  };

  return (
    <div>
      <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <h1 style={{ margin: 0 }}>Quản lý Dự án</h1>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={handleCreate}
          size="large"
        >
          Thêm dự án mới
        </Button>
      </div>

      {/* Thống kê */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Tổng số dự án"
              value={stats.total}
              prefix={<ThunderboltOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Đang hoạt động"
              value={stats.active}
              prefix={<PoweroffOutlined />}
              valueStyle={{ color: '#3f8600' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Tổng công suất PV (kW)"
              value={stats.totalPvCapacity}
              precision={2}
              valueStyle={{ color: '#faad14' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Tổng công suất Inverter (kW)"
              value={stats.totalInverterCapacity}
              precision={2}
              valueStyle={{ color: '#cf1322' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Bảng danh sách projects */}
      <Table
        columns={columns}
        dataSource={projects}
        rowKey="id"
        loading={loading}
        size="middle"
        tableLayout="auto"
        scroll={{ x: 900 }}
        pagination={{
          pageSize: 10,
          showSizeChanger: true,
          showTotal: (total) => `Tổng ${total} dự án`,
        }}
      />

      {/* Modal thêm/sửa project */}
      <Modal
        title={editingProject ? 'Sửa dự án' : 'Thêm dự án mới'}
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
        }}
        footer={null}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Form.Item
            label="Tên dự án"
            name="name"
            rules={[{ required: true, message: 'Vui lòng nhập tên dự án' }]}
          >
            <Input placeholder="Nhập tên dự án" />
          </Form.Item>

          <Form.Item
            label="Địa chỉ"
            name="address"
          >
            <Input placeholder="Nhập địa chỉ" />
          </Form.Item>

          <Form.Item
            label="Số điện thoại"
            name="phone_number"
            rules={[
              {
                pattern: /^[0-9+\-\s]{8,20}$/,
                message: 'Số điện thoại không hợp lệ'
              }
            ]}
          >
            <Input placeholder="Nhập số điện thoại" />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Tổng công suất PV (kW)"
                name="total_pv_capacity"
                rules={[
                  {
                    validator: (_, value) => {
                      if (value === undefined || value === null || value === '') return Promise.resolve();
                      const num = Number(value);
                      if (isNaN(num) || num < 0) return Promise.reject('Phải là số không âm');
                      return Promise.resolve();
                    }
                  }
                ]}
              >
                <InputNumber
                  min={0}
                  step={0.01}
                  style={{ width: '100%' }}
                  placeholder="0.00"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Tổng công suất Inverter (kW)"
                name="total_inverter_capacity"
                rules={[
                  {
                    validator: (_, value) => {
                      if (value === undefined || value === null || value === '') return Promise.resolve();
                      const num = Number(value);
                      if (isNaN(num) || num < 0) return Promise.reject('Phải là số không âm');
                      return Promise.resolve();
                    }
                  }
                ]}
              >
                <InputNumber
                  min={0}
                  step={0.01}
                  style={{ width: '100%' }}
                  placeholder="0.00"
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            label="Hình ảnh dự án (URL)"
            name="image_url"
            rules={[
              {
                type: 'url',
                message: 'URL không hợp lệ'
              }
            ]}
          >
            <Input placeholder="https://example.com/image.jpg" />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Vĩ độ (latitude)"
                name={['location', 'latitude']}
                rules={[
                  {
                    validator: (_, value) => {
                      if (value === undefined || value === null || value === '') return Promise.resolve();
                      const num = Number(value);
                      if (isNaN(num) || num < -90 || num > 90) return Promise.reject('Latitude phải trong [-90, 90]');
                      return Promise.resolve();
                    }
                  }
                ]}
              >
                <InputNumber style={{ width: '100%' }} placeholder="10.123456" step={0.000001} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Kinh độ (longitude)"
                name={['location', 'longitude']}
                rules={[
                  {
                    validator: (_, value) => {
                      if (value === undefined || value === null || value === '') return Promise.resolve();
                      const num = Number(value);
                      if (isNaN(num) || num < -180 || num > 180) return Promise.reject('Longitude phải trong [-180, 180]');
                      return Promise.resolve();
                    }
                  }
                ]}
              >
                <InputNumber style={{ width: '100%' }} placeholder="106.123456" step={0.000001} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            label="Trạng thái"
            name="status"
            initialValue="active"
          >
            <Select>
              <Option value="active">Hoạt động</Option>
              <Option value="inactive">Tạm dừng</Option>
              <Option value="completed">Hoàn thành</Option>
              <Option value="cancelled">Hủy</Option>
            </Select>
          </Form.Item>

          <Form.Item
            label="Mô tả"
            name="description"
          >
            <TextArea rows={4} placeholder="Nhập mô tả dự án" />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={loading}>
                {editingProject ? 'Cập nhật' : 'Tạo mới'}
              </Button>
              <Button onClick={() => {
                setModalVisible(false);
                form.resetFields();
              }}>
                Hủy
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Drawer chi tiết project và quản lý inverters */}
      <Drawer
        title={`Chi tiết dự án: ${selectedProject?.name || ''}`}
        placement="right"
        width={drawerWidth}
        open={drawerVisible}
        onClose={() => {
          setDrawerVisible(false);
          setSelectedProject(null);
          setInverters([]);
        }}
      >
        {selectedProject && (
          <>
            <Descriptions title="Thông tin dự án" bordered column={1}>
              <Descriptions.Item label="Tên dự án">{selectedProject.name}</Descriptions.Item>
              <Descriptions.Item label="Địa chỉ">
                <Space>
                  <EnvironmentOutlined />
                  {selectedProject.address || 'Chưa có'}
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label="Số điện thoại">
                <Space>
                  <PhoneOutlined />
                  {selectedProject.phone_number || 'Chưa có'}
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label="Tổng công suất PV">
                {selectedProject.total_pv_capacity ? `${parseFloat(selectedProject.total_pv_capacity).toFixed(2)} kW` : '0.00 kW'}
              </Descriptions.Item>
              <Descriptions.Item label="Tổng công suất Inverter">
                {selectedProject.total_inverter_capacity ? `${parseFloat(selectedProject.total_inverter_capacity).toFixed(2)} kW` : '0.00 kW'}
              </Descriptions.Item>
              <Descriptions.Item label="Số lượng Inverter">
                <Tag color="blue">{selectedProject.inverter_count || 0}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Trạng thái">
                <Tag color={selectedProject.status === 'active' ? 'green' : 'default'}>
                  {selectedProject.status === 'active' ? 'Hoạt động' : selectedProject.status}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Hình ảnh">
                {selectedProject.image_url ? (
                  <a href={selectedProject.image_url} target="_blank" rel="noreferrer">Xem hình</a>
                ) : 'Chưa có'}
              </Descriptions.Item>
              <Descriptions.Item label="Toạ độ">
                {selectedProject.location?.latitude && selectedProject.location?.longitude
                  ? `${selectedProject.location.latitude}, ${selectedProject.location.longitude}`
                  : 'Chưa có'}
              </Descriptions.Item>
            </Descriptions>

            <Divider>Quản lý Inverter</Divider>

            <div style={{ marginBottom: 16 }}>
              <Select
                style={{ width: '100%' }}
                placeholder="Chọn inverter để thêm vào dự án"
                showSearch
                optionFilterProp="children"
                filterOption={(input, option) =>
                  option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                }
                onChange={handleAddInverter}
                value={null}
              >
                {availableInverters.map(inv => (
                  <Option key={inv.id} value={inv.id}>
                    {inv.name} - SN: {inv.serial_number}
                  </Option>
                ))}
              </Select>
            </div>

            <Table
              columns={[
                {
                  title: 'Tên',
                  dataIndex: 'name',
                  key: 'name',
                },
                {
                  title: 'Serial Number',
                  dataIndex: 'serial_number',
                  key: 'serial_number',
                },
                {
                  title: 'Model',
                  dataIndex: 'model',
                  key: 'model',
                },
                {
                  title: 'Trạng thái',
                  dataIndex: 'status',
                  key: 'status',
                  render: (status) => {
                    const statusMap = {
                      online: { color: 'green', text: 'Online' },
                      offline: { color: 'default', text: 'Offline' },
                      maintenance: { color: 'orange', text: 'Bảo trì' },
                      error: { color: 'red', text: 'Lỗi' },
                    };
                    const statusInfo = statusMap[status] || { color: 'default', text: status };
                    return <Tag color={statusInfo.color}>{statusInfo.text}</Tag>;
                  },
                },
                {
                  title: 'Thao tác',
                  key: 'action',
                  render: (_, record) => (
                    <Popconfirm
                      title="Bạn có chắc muốn xóa inverter khỏi dự án này?"
                      onConfirm={() => handleRemoveInverter(record.id)}
                      okText="Xóa"
                      cancelText="Hủy"
                    >
                      <Button type="link" danger size="small">
                        Xóa khỏi dự án
                      </Button>
                    </Popconfirm>
                  ),
                },
              ]}
              dataSource={inverters}
              rowKey="id"
              pagination={false}
              size="small"
            />
          </>
        )}
      </Drawer>
    </div>
  );
}

